/**
 * useSessionOrder - Manage session card order with drag-and-drop
 *
 * Features:
 * - Maintains custom order for watched sessions
 * - Persists to sessionStorage (clears on tab close)
 * - Auto-adds new sessions at the end
 * - Auto-removes deleted sessions
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import type { SessionInfo } from './useSessionMonitor'

interface UseSessionOrderOptions {
  sessions: SessionInfo[]
  storageKey?: string
}

export function useSessionOrder({
  sessions,
  storageKey = 'logsession-monitor-order'
}: UseSessionOrderOptions) {
  const [order, setOrder] = useState<string[]>([])

  // Initialize from storage only (sessions sync handled by next useEffect)
  useEffect(() => {
    const stored = sessionStorage.getItem(storageKey)
    if (stored) {
      try {
        const parsed = JSON.parse(stored)
        if (Array.isArray(parsed)) {
          setOrder(parsed)
        }
      } catch {
        // Invalid JSON, ignore
      }
    }
  }, [storageKey])

  // Update when sessions change (add new at end, remove deleted)
  useEffect(() => {
    setOrder(prev => {
      const sessionNames = new Set(sessions.map(s => s.name))

      // Keep existing order for known sessions
      const kept = prev.filter(name => sessionNames.has(name))

      // Add new sessions at the end
      const newSessions = sessions
        .map(s => s.name)
        .filter(name => !prev.includes(name))

      if (newSessions.length === 0 && kept.length === prev.length) {
        return prev // No changes
      }

      return [...kept, ...newSessions]
    })
  }, [sessions])

  // Persist on change
  useEffect(() => {
    if (order.length > 0) {
      sessionStorage.setItem(storageKey, JSON.stringify(order))
    }
  }, [order, storageKey])

  // Reorder handler for drag-and-drop
  const reorder = useCallback((activeId: string, overId: string) => {
    setOrder(prev => {
      const oldIndex = prev.indexOf(activeId)
      const newIndex = prev.indexOf(overId)

      if (oldIndex === -1 || newIndex === -1) return prev
      if (oldIndex === newIndex) return prev

      const newOrder = [...prev]
      newOrder.splice(oldIndex, 1)
      newOrder.splice(newIndex, 0, activeId)
      return newOrder
    })
  }, [])

  // Get sessions in custom order
  const orderedSessions = useMemo(() =>
    order
      .map(name => sessions.find(s => s.name === name))
      .filter((s): s is SessionInfo => s !== undefined),
    [order, sessions]
  )

  return {
    orderedSessions,
    reorder,
    order
  }
}
